import { memo, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { useTranslation } from "react-i18next";
import { useAsyncReducer } from "store/selectTemplate";
import getCompanyProjects from "api/handlers/project/getProjectsCompany";
import actions from "store/manuals/actions";
import SelectGroupSearch from "components/select/selectGroupSearch";
import { createSelector } from "reselect";
import isEqual from "react-fast-compare";

const selector = createSelector(
  (state: AppState) => state.manuals.modalProject,
  (modalProject: any) => {
    return modalProject;
  }
);

const CompaniesAndProjectsModal = memo(({ disabled = false, company }: any) => {
  const dispatch: any = useDispatch();
  const { t } = useTranslation();
  const [searchPhrase, setSearchPhrase] = useState("");
  const { anonymization } = useSelector((state: AppState) => state.auth);

  const modalProject: any = useSelector(selector, isEqual);

  const { data, loading, loadingMore, loadMore } = useAsyncReducer(
    "projectsModalSelect",
    getCompanyProjects,
    {
      pageSize: 100,
      q: searchPhrase,
      ids: modalProject ? [modalProject] : undefined,
      company,
      anonymization,
    }
  );

  const groupedProjects = useMemo(() => {
    return data
      ? data.results.reduce((acc: any[], project: any) => {
          const companyId = project.company;

          const existingGroup =
            acc && acc?.find((item: any) => item.group === companyId);

          if (existingGroup) {
            existingGroup.items.push(project);
          } else {
            acc.push({ group: companyId, items: [project] });
          }

          return acc;
        }, [])
      : [];
  }, [data]);

  const updatedMergedProjects = useMemo(() => {
    return groupedProjects.map((project: any) => ({
      ...project,
      group: project.items[0].companyName,
      items: project.items.map((item: any) => ({
        name: item.name,
        id: item.id,
      })),
    }));
  }, [groupedProjects]);

  const onChangeModalSelect = useCallback(
    (event: any) => {
      dispatch(actions.setModalProject(Number(event.target.value)));
    },
    [dispatch]
  );

  return (
    <>
      <SelectGroupSearch
        id="projectsModalSelect"
        label={t("manualModal.manualHierarchy.project.label")}
        name="projectsModalSelect"
        options={updatedMergedProjects}
        value={modalProject ? modalProject.toString() : ""}
        onChange={onChangeModalSelect}
        search={searchPhrase}
        onSearch={setSearchPhrase}
        searchPlaceholder={`${t("search.searchCompanyAndProjects")}`}
        disabled={disabled}
        loadMore={loadMore}
        loadingMore={loadingMore}
        loading={!data || loading}
        required
      />
    </>
  );
});

export default CompaniesAndProjectsModal;
